import { ICON__check } from '@/assets/icons_fa';
import { UI_PRIMITIVE__button } from '@/components/ui/UI_PRIMITIVE__button';
import { UI_PRIMITIVE__input } from '@/components/ui/UI_PRIMITIVE__input';
import { ENUM__html_ref_stub } from '@/enums/ENUM__html_ref_stub';
import { Key } from '@/enums/key';
import { use_dispatch } from '@/hooks/_base/use_dispatch';
import { use_ref_with_fallback } from '@/hooks/_base/use_ref_with_fallback';
import { use_slice_workspace } from '@/hooks/store/use_slices';
import { useToast } from '@/hooks/use-toast';
import i18n_base_ui from '@/locales/keys/i18n_base_ui.json';
import i18n_page_settings_workspace from '@/locales/keys/i18n_page_settings_workspace.json';
import { ENUM__i18n_namespace } from '@/services/i18n/ENUM__i18n_namespace';
import { remote_workspace_user_invitation_create } from '@/state/workspace/user_invitation/remote_workspace_user_invitation_create';
import { md } from '@/utils/utils_html';
import { RequestWorkspaceUserInvitationCreate } from '@shared/dto/+workspace/workspace_user_invitation/_requests/request_workspace_user_invitation_create';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

export function WInputUserInvitationEmail() {
  const { t } = useTranslation([ENUM__i18n_namespace.PAGE_SETTINGS_WORKSPACE, ENUM__i18n_namespace.BASE_UI]);

  const dispatch = use_dispatch();
  const { toast } = useToast();

  const { workspace_current } = use_slice_workspace(['workspace_current']);

  const [inputValue, setInputValue] = useState('');

  const [is_disabled, set_disabled] = useState(false);

  const ref_input = use_ref_with_fallback(ENUM__html_ref_stub.INPUT);

  /*
   * EVENTS
   */

  async function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === Key.Enter) {
      await onEnterPressed();
    }

    if (e.key === Key.Escape) {
      ref_input.current.blur();
    }
  }

  async function onEnterPressed() {
    let email = inputValue.trim();

    if (z.string().email().safeParse(email).success) {
      let request = new RequestWorkspaceUserInvitationCreate();
      request.workspace_id = workspace_current._id;
      request.target_user_email = email;

      set_disabled(true);
      await dispatch(remote_workspace_user_invitation_create(request));
      set_disabled(false);

      setInputValue('');
    } else {
      toast({ title: t(i18n_base_ui.toasts.input_user_invitation_email_invalid.title) });
    }
  }

  /*
   * RENDERS
   */

  function render_icon() {
    if (inputValue.length > 0) {
      return (
        <UI_PRIMITIVE__button
          className=" absolute top-2.5 right-3"
          size="square-5"
          variant="no-border-on-component"
          onClick={onEnterPressed}
          disabled={is_disabled}
        >
          <ICON__check className="!size-3 !text-component-item-foreground" />
        </UI_PRIMITIVE__button>
      );
    }

    return <></>;
  }

  if (workspace_current.is_personal) return <></>;

  return (
    <div className={`relative ${md('w-full', 'w-[30rem]')}`}>
      <UI_PRIMITIVE__input
        disabled={is_disabled}
        value={inputValue}
        ref={ref_input}
        placeholder={t(i18n_page_settings_workspace.tabs.users.shared.invite_by_email_component.input.email.placeholder)}
        maxLength={30}
        onChange={e => setInputValue(e.target.value)}
        {...{ onKeyDown }}
        className={`w-full h-10 cursor-text bg-component border border-component-border hover-animation duration-300 rounded-lg !outline-none focus:outline-none  px-3  md:text-base !text-component-item-foreground placeholder-component-item-foreground ${
          is_disabled ? 'bg-component-item-selected' : 'bg-component'
        }`}
      />
      {render_icon()}
    </div>
  );
}
